import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { userId, type UserId } from './User.js'

/** Identifier type for public.user_tos_acceptance_ */
export type UserTosAcceptanceId = string & { __brand: 'UserTosAcceptanceId' }

/** Represents the table public.user_tos_acceptance_ */
export default interface UserTosAcceptanceTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    UserTosAcceptanceId,
    UserTosAcceptanceId | undefined,
    UserTosAcceptanceId
  >

  /** Database type: pg_catalog.uuid -- nullable for pseudonymized records */
  userId: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  tosVersion: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  acceptedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.inet -- nullable if request IP unavailable */
  ipAddress: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text -- nullable if user-agent header absent */
  userAgent: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.bool */
  newsletterOptedIn: ColumnType<boolean, boolean, boolean>

  /** Database type: pg_catalog.timestamptz -- null until erasure pseudonymization */
  purgeAfter: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >
}

export type UserTosAcceptance = Selectable<UserTosAcceptanceTable>
export type UserTosAcceptanceInitializer = Insertable<UserTosAcceptanceTable>
export type UserTosAcceptanceMutator = Updateable<UserTosAcceptanceTable>

export const userTosAcceptanceId =
  z.string() as unknown as z.Schema<UserTosAcceptanceId>

export const userTosAcceptance = z.object({
  id: userTosAcceptanceId,
  userId: userId.nullable(),
  tosVersion: z.string(),
  acceptedAt: z.coerce.date(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  newsletterOptedIn: z.boolean(),
  purgeAfter: z.coerce.date().nullable(),
}) as unknown as z.Schema<UserTosAcceptance>

export const userTosAcceptanceInitializer = z.object({
  id: userTosAcceptanceId.optional(),
  userId: userId.optional().nullable(),
  tosVersion: z.string(),
  acceptedAt: z.coerce.date().optional(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  newsletterOptedIn: z.boolean(),
  purgeAfter: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<UserTosAcceptanceInitializer>

export const userTosAcceptanceMutator = z.object({
  id: userTosAcceptanceId.optional(),
  userId: userId.optional().nullable(),
  tosVersion: z.string().optional(),
  acceptedAt: z.coerce.date().optional(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  newsletterOptedIn: z.boolean().optional(),
  purgeAfter: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<UserTosAcceptanceMutator>
